import React, { useMemo, useState } from "react";

import {
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { partition } from "lodash";

import { useTaggerActions, useTaggerDocumentIdsSelector, useTaggerTagTableSelector } from "../../../store";
import { IProcessedTag, TagTableRow } from "../../../store/types";
import { TagRow } from "../TagRow/TagRow";

export type TagMethod = (
  deal: TagTableRow["deal"],
  tagId: IProcessedTag["id"],
  is_child: boolean,
  hasChildren: boolean
) => void;

export interface TagTableProps {
  onEditTag: TagMethod;
}

export const TagTable: React.FC<TagTableProps> = ({ onEditTag }) => {
  const rows = useTaggerTagTableSelector();
  const documents = useTaggerDocumentIdsSelector();
  const { tagAsDraft, tagSplitAsDraft, splitTagByInvestor, splitTagByCommitment } = useTaggerActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rowData, setRowData] = useState<null | TagTableRow>(null);
  const [shownRows] = useMemo(() => partition(rows, (row) => row.enabled), [documents, rows]);
  const nonShownRows: TagTableRow[] = []; // Product decision to revert Non-shown rows.

  const documentSeriesId = useMemo(() => {
    return rows.find((row) => row.deal.id === rowData?.deal.id)?.series_id || null;
  }, [documents, rowData]);

  const handleTagAsDraft = () => {
    if (!rowData?.is_child && rowData?.children) {
      tagSplitAsDraft({
        dealId: rowData.deal.id,
        seriesId: rowData.series_id || documentSeriesId,
        splitId: rowData.split_id,
        is_draft: !rowData.is_draft
      });
    } else {
      rowData &&
        tagAsDraft({
          dealId: rowData.deal.id,
          seriesId: rowData.series_id || documentSeriesId,
          tagId: rowData.id,
          is_draft: !rowData.is_draft
        });
    }
    setAnchorEl(null);
  };

  return (
    <TableContainer
      sx={{
        flexGrow: "1",
        marginBottom: "12px",
        boxShadow:
          "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px"
      }}
    >
      <Table size="small">
        <TableHead sx={{ position: "sticky", top: "0", backgroundColor: "#F6F8FC", zIndex: "1" }}>
          <TableRow>
            <TableCell />
            <TableCell>Parameter</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Date</TableCell>
            <TableCell sx={{ width: "110px" }}>Round</TableCell>
            <TableCell>Investor</TableCell>
            <TableCell>Accounting Date</TableCell>
            <TableCell>Deal</TableCell>
            <TableCell sx={{ width: "110px" }}>File Name</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {shownRows.map((data, index) => {
            return (
              <TagRow
                onEditTag={onEditTag}
                setAnchorEl={setAnchorEl}
                setRowData={setRowData}
                key={index}
                row={data}
                availableTag
              />
            );
          })}
          {nonShownRows.length ? (
            <TableRow sx={{ height: "42px", borderBottom: "1px solid #DBE8F5 !important" }}>
              <TableCell sx={{ position: "relative", borderBottom: "1px solid #DBE8F5 !important" }}>
                <Typography sx={{ position: "absolute", top: "10px", fontSize: "14px", color: "#071F35" }}>
                  Not found in current PDF
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
          {nonShownRows.map((data, index) => {
            return (
              <TagRow
                onEditTag={onEditTag}
                setAnchorEl={setAnchorEl}
                setRowData={setRowData}
                key={index}
                row={data}
                availableTag={false}
              />
            );
          })}
        </TableBody>
      </Table>
      <Menu
        id="tag-table-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setRowData(null);
        }}
      >
        {rowData?.is_splittable && (
          <MenuItem
            onClick={() => {
              splitTagByCommitment(rowData.deal.id, rowData.series_id || documentSeriesId, rowData.id);
              setAnchorEl(null);
            }}
          >
            Split by Commitment
          </MenuItem>
        )}
        {rowData && rowData.investor?.id && rowData.is_splittable && (
          <MenuItem
            onClick={() => {
              if (!rowData.investor?.id) return;
              splitTagByInvestor(
                rowData.deal.id,
                rowData.series_id || documentSeriesId,
                rowData.id,
                rowData.investor.id
              );
              setAnchorEl(null);
            }}
          >
            From Single Investor
          </MenuItem>
        )}
        {!rowData?.measure_data_id && !rowData?.draft_measure_data_id && (
          <MenuItem onClick={handleTagAsDraft}>{rowData?.is_draft ? "Untag as a draft" : "Tag as draft"}</MenuItem>
        )}
      </Menu>
    </TableContainer>
  );
};
